/**
 * Data display — from the Dashboard (headline figures, legends, meters) and AI Briefing (list rows).
 * Literals verbatim; see `kit.ts` for the canonical sources.
 */
import type { ReactNode } from 'react';
import { css, s } from './style';
import { SECTION_LABEL } from './surfaces';
import type { Primitive } from './controls';

/** Headline figure — the Dashboard's Net P&L (line 257: 28px) and card figures (line 431: 22px). */
export function HeadlineFigure({
  size = 22,
  color = 'var(--text)',
  children,
  extra,
}: {
  size?: number;
  color?: string;
  children?: ReactNode;
  extra?: string;
}) {
  return (
    <div
      style={css(
        `font-size:${size}px;font-weight:600;letter-spacing:-0.02em;font-variant-numeric:tabular-nums;line-height:1.1;color:${color}${extra ? ';' + extra : ''}`,
      )}
    >
      {children}
    </div>
  );
}
/** Label + figure pair — the Dashboard's stat block (lines 256–257). */
export function StatTile({
  label,
  value,
  sub,
  valueColor = 'var(--text)',
  size = 22,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  valueColor?: string;
  size?: number;
}) {
  return (
    <div>
      <div style={css(SECTION_LABEL)}>{label}</div>
      <div
        style={css(
          `font-size:${size}px;font-weight:600;letter-spacing:-0.02em;font-variant-numeric:tabular-nums;margin-top:2px;color:${valueColor};line-height:1.1`,
        )}
      >
        {value}
      </div>
      {sub === undefined ? null : (
        <div style={css('font-size:10.5px;color:var(--muted);margin-top:3px')}>{sub}</div>
      )}
    </div>
  );
}
/** Legend item — the Dashboard's calendar legend (line 494, ×4). */
export function Legend({ swatch, children }: { swatch?: ReactNode; children?: ReactNode }) {
  return (
    <span style={css('display:inline-flex;align-items:center;gap:6px;font-size:10.5px;color:var(--muted)')}>
      {swatch}
      {children}
    </span>
  );
}
/** Legend swatch — 10×10 tinted square. */
export function LegendSwatch({ bg, bd, img }: { bg: Primitive; bd?: Primitive; img?: Primitive }) {
  return (
    <span
      style={css(
        `width:10px;height:10px;border-radius:3px;background:${s(bg)};border:1px solid ${s(bd) || 'transparent'};background-image:${s(img) || 'none'};background-size:6px 6px;flex:none`,
      )}
    ></span>
  );
}
/** Meter — the scale is derived from the value, never asserted (design CLAUDE.md, §9.4). */
export function Meter({
  fill,
  color = 'var(--pos)',
  height = 6,
  track = 'var(--l7)',
}: {
  fill: string;
  color?: string;
  height?: number;
  track?: string;
}) {
  return (
    <div style={css(`height:${height}px;border-radius:99px;background:${track};overflow:hidden`)}>
      <div style={css(`width:${fill};height:100%;border-radius:99px;background:${color}`)}></div>
    </div>
  );
}
/** List row — the Briefing's calendar rows (line 786). */
export function ListRow({ children, last }: { children?: ReactNode; last?: boolean }) {
  return (
    <div
      style={css(
        `display:flex;align-items:center;gap:12px;padding:10px 4px${last ? '' : ';border-bottom:1px solid var(--l5)'}`,
      )}
    >
      {children}
    </div>
  );
}
/** Tabular time/figure cell — the Briefing's row time (line 787). */
export function TabularCell({
  width,
  color = 'var(--muted)',
  children,
}: {
  width?: number;
  color?: string;
  children?: ReactNode;
}) {
  return (
    <span
      style={css(
        `font-size:11.5px;color:${color};font-variant-numeric:tabular-nums${width ? `;width:${width}px` : ''}`,
      )}
    >
      {children}
    </span>
  );
}
/** Key tag — the Briefing's currency tag (line 788). */
export function KeyTag({ children }: { children?: ReactNode }) {
  return (
    <span
      style={css(
        'font-size:10px;font-weight:600;letter-spacing:0.05em;padding:2px 7px;border-radius:6px;background:var(--l6);color:var(--text-mid)',
      )}
    >
      {children}
    </span>
  );
}
